import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {AvailabilityPane} from 'components/AvailabilityCalendarPane'
import {AvailabilityCalendarTrigger} from 'components/AvailabilityCalendarPane/AvailabilityCalendarTrigger'
import {differenceInDays} from 'date-fns/differenceInDays'
import {parseISO} from 'date-fns/parseISO'
import {
  getHotelAvailabilityPrices,
  getHotelAvailabilityPricesComplete,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {useSapiSearch} from 'modules/sapiSearch/useSapiSearch'
import {getSelectedAvailabilityDate} from 'modules/search/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {
  getDatesToDisplay,
  MAX_DATES_TO_SHOW_MOBILE
} from '@vio/core/src/_web/availability'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'

import {Loading} from './Loading'

interface Props {
  hotelId: string
  sourceComponentPrefix: string
}

const AvailableDatesLabel = styled(Text)`
  margin-top: ${cssTheme.layout.spacing.s200};
  margin-bottom: ${cssTheme.layout.spacing.s300};
`

const SoldOutWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s100} 0;
  display: grid;
  gap: ${cssTheme.layout.spacing.s100};
  grid-template-columns: min-content auto;
  align-items: start;
`

const TextWrapper = styled.div`
  flex-shrink: 1;
`

const DateWrapper = styled.span`
  white-space: nowrap;
`

export const UnavailabilityMobile = ({
  hotelId,
  sourceComponentPrefix
}: Props) => {
  const dispatch = useDispatch()
  const {formatDate} = useIntl()
  const {checkIn, checkOut} = useSelector(getSearchParameters)
  const selectedAvailabilityDate = useSelector(getSelectedAvailabilityDate)

  const [initialSearchComplete, setInitialSearchComplete] =
    useState<boolean>(false)

  const hotelAvailabilityPrices = useSelector(state =>
    getHotelAvailabilityPrices(state, hotelId)
  )

  const selectedDateOffers =
    hotelAvailabilityPrices &&
    hotelAvailabilityPrices[selectedAvailabilityDate]?.offers

  const datesToDisplay =
    hotelAvailabilityPrices &&
    getDatesToDisplay(
      hotelAvailabilityPrices,
      checkIn,
      MAX_DATES_TO_SHOW_MOBILE
    )

  const availabilitySearchComplete = useSelector(
    getHotelAvailabilityPricesComplete
  )

  const nights =
    datesToDisplay && differenceInDays(parseISO(checkOut), parseISO(checkIn))

  useEffect(() => {
    if (availabilitySearchComplete) {
      // We must preserve initial search state to avoid switching between `loading` and content
      // when user press `See all dates` button
      setInitialSearchComplete(true)
    }
  }, [availabilitySearchComplete])

  useEffect(() => {
    if (!datesToDisplay) {
      {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.NoRoomsAvailableMessage,
            action: Action.Viewed
          })
        )
      }
    }
  }, [datesToDisplay, dispatch])

  const {useGetHotelAvailability} = useSapiSearch()
  const {getMonthlyAvailability} = useGetHotelAvailability({hotelId})

  return (
    <>
      {initialSearchComplete && (
        <>
          <Box
            display="flex"
            gap="s500"
            alignItems="flex-start"
            justifyContent="space-between"
            data-id="SoldOutPane"
          >
            <SoldOutWrapper>
              <Box paddingTop="s100" paddingBottom="s100">
                <Icon
                  size="md"
                  name="Forbid"
                  colorPath="content.danger.secondary"
                />
              </Box>
              <TextWrapper>
                <Text variant="titleM" dataId="soldOut">
                  <FormattedMessageWrapper
                    id="soldOut.messageWithDatesB"
                    defaultMessage="Sold out {checkIn} - {checkOut}"
                    values={{
                      checkIn: (
                        <DateWrapper>
                          {formatDate(dateStringToMiddayDate(checkIn), {
                            day: '2-digit',
                            month: 'short'
                          })}
                        </DateWrapper>
                      ),
                      checkOut: (
                        <DateWrapper>
                          {formatDate(dateStringToMiddayDate(checkOut), {
                            day: '2-digit',
                            month: 'short'
                          })}
                        </DateWrapper>
                      )
                    }}
                  />
                </Text>
              </TextWrapper>
            </SoldOutWrapper>
            {datesToDisplay && (
              <AvailabilityCalendarTrigger
                hotelId={hotelId}
                fetchMonthlyPrices={getMonthlyAvailability}
                hotelAvailabilityPrices={hotelAvailabilityPrices}
                selectedDate={selectedAvailabilityDate}
                sourceComponentPrefix={sourceComponentPrefix}
                offersData={selectedDateOffers}
                nights={nights}
                checkIn={checkIn}
                availabilitySearchComplete={availabilitySearchComplete}
              />
            )}
          </Box>
          {datesToDisplay ? (
            <>
              <AvailableDatesLabel variant="bodyS">
                <FormattedMessageWrapper
                  id="checkOutAvailableDates"
                  defaultMessage="Check out available dates"
                />
              </AvailableDatesLabel>

              <AvailabilityPane
                hotelId={hotelId}
                hotelAvailabilityPrices={hotelAvailabilityPrices}
                sourceComponentPrefix={sourceComponentPrefix}
                datesToDisplay={datesToDisplay}
                availabilitySearchComplete={availabilitySearchComplete}
              />
            </>
          ) : (
            <AvailableDatesLabel variant="bodyS">
              <Box paddingBottom="s200">
                <FormattedMessageWrapper
                  id="noMoreRoomsAvailable"
                  defaultMessage="There are no rooms available at this property."
                />
              </Box>
            </AvailableDatesLabel>
          )}
        </>
      )}
      {!initialSearchComplete && <Loading />}
    </>
  )
}
